import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { RPN } from '@nxsys/store/slices/rpn/types';
import { Icons } from '@nxsys/types/icons';
import { IAepn } from '@nxsys/types/models/aepn';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';

export const getColumns = ({
  messages,
  isErrorTab = false,
  onClickView,
}: {
  messages: TypedMessages;
  isErrorTab?: boolean;
  onClickView: (data: RPN) => void;
}): ColumnsType<IAepn> => {
  return [
    ...(!isErrorTab
      ? [
          {
            key: 'action',
            title: messages['common.action'],
            fixed: 'left',
            align: 'left',
            render: (record: any) => (
              <ul className="table-actions flex items-center justify-center">
                <li onClick={() => onClickView(record)}>
                  <Tooltip title="View">
                    <span><Icons.IconEye /></span>
                  </Tooltip>
                </li>
              </ul>
            ),
            width: 100,
          },
        ]
      : []),
    {
      key: 'employeeId',
      title: <span>{messages['common.employeeId']}</span>,
      dataIndex: 'subcontractor',
      render: (value, record) => (
        <div className="row_center">{value?.id || record?.subcontractorId || '_'}</div>
      ),
      width: 150,
    },
    {
      key: 'employeeName',
      title: <span>{messages['common.employeeName']}</span>,
      dataIndex: 'subcontractor',
      render: (value, record) => (
        <div className="row_center">
          {`${value?.forename || record?.employeeFirstName || '_'} ${value?.surname || record?.employeeFamilyName || '_'}`}
        </div>
      ),
      width: 250,
    },
    {
      key: 'agencyRef',
      title: <span>{messages['common.employerRef']}</span>,
      dataIndex: 'subcontractor',
      render: (value) => <div className="row_center">{value?.agencyRef || '_'}</div>,
      width: 150,
    },
    {
      key: 'employeePPSN',
      title: <span>PPS Number</span>,
      dataIndex: 'employeePPSN',
      render: (value) => (
        <div className="row_center">{value || '_'}</div>
      ),
      width: 150,
    },
    {
      key: 'employmentID',
      title: <span>Employment ID</span>,
      dataIndex: 'employmentID',
      render: (value) => (
        <div className="row_center">{value || '_'}</div>
      ),
      width: 150,
    },
    {
      key: 'companyName',
      title: <span>Company name</span>,
      dataIndex: 'company',
      render: (value) => <div className="row_center">{value?.companyName || '_'}</div>,
      width: 250,
    },
    {
      key: 'employerRegistrationNumber',
      title: <span>Employer Registration Number</span>,
      dataIndex: 'employerRegistrationNumber',
      render: (value) => <div className="row_center">{value || '_'}</div>,
      width: 350,
    },
    {
      key: 'taxYear',
      title: <span>TaxYear</span>,
      align: 'right',
      dataIndex: 'taxYear',
      render: (value) => <div className="row_center">{value || '_'}</div>,
      width: 150,
    },
    {
      key: 'aepnNumber',
      title: <span>AEPN Number</span>,
      dataIndex: 'aepnNumber',
      render: (value) => (
        <div className="row_center">{value || '_'}</div>
      ),
      width: 150,
    },
    {
      key: 'enrolmentStatus',
      title: <span>Enrolment Status</span>,
      align: 'center',
      dataIndex: 'enrolmentStatus',
      render: (value) => (
        <div className="row_center">{value || '_'}</div>
      ),
      width: 150,
    },
    {
      key: 'aepnEnrolmentDate',
      title: <span>AEPN Enrolment Date</span>,
      align: 'right',
      dataIndex: 'aepnEnrolmentDate',
      render: (value) => (
        <div className="row_center">
          {value ? moment(value).format('DD/MM/YYYY') : '_'}
        </div>
      ),
      width: 250,
    },
    {
      key: 'aepnChangeEvent',
      title: <span>AEPN Change Event</span>,
      dataIndex: 'aepnChangeEvent',
      render: (value) => (
        <div className="row_center">{value || '_'}</div>
      ),
      width: 250,
    },
    {
      key: 'eeContributionRate',
      title: <span>EE Contribution Rate</span>,
      align: 'right',
      dataIndex: 'eeContributionRate',
      render: (value) => {
        return <div className="row_center">{value || 0.00}</div>;
      },
      width: 250,
    },
    {
      key: 'erContributionRate',
      title: <span>ER Contribution Rate</span>,
      align: 'right',
      dataIndex: 'erContributionRate',
      render: (value) => {
        return <div className="row_center">{value || 0.00}</div>;
      },
      width: 250,
    },
    {
      key: 'aepnIssueDate',
      title: <span>AEPN Issue Date</span>,
      align: 'right',
      dataIndex: 'aepnIssueDate',
      render: (value) => (
        <div className="row_center">
          {value ? moment(value).format('DD/MM/YYYY') : '_'}
        </div>
      ),
      width: 250,
    },
    {
      key: 'aepnDownloadDateTime',
      title: <span>AEPN Download Date</span>,
      align: 'right',
      dataIndex: 'aepnDownloadDateTime',
      render: (value) => (
        <div className="row_center">
          {value ? moment(value).format('DD/MM/YYYY') : '_'}
        </div>
      ),
      width: 250,
    },
    {
      key: 'type',
      title: <span>Type</span>,
      align: 'center',
      dataIndex: 'type',
      render: (value) => (
        <div className="row_center">{value || '_'}</div>
      ),
      width: 'auto',
    },
    {
      key: 'updatedAt',
      title: <span>Last Updated</span>,
      align: 'right',
      dataIndex: 'updatedAt',
      render: (value) => (
        <div className="row_center">{value ? moment(value).format('DD/MM/YYYY') : '_'}</div>
      ),
      width: 'auto',
    },
    ...(isErrorTab
      ? [
          {
            key: 'errorMessage',
            title: messages['rpn.errorMessage'],
            dataIndex: 'errorMessage',
            width: 400,
          },
        ]
      : []),
  ] as ColumnsType<any>;
};
